import type { User } from '../../../lib/flotiq-client/types/api-response';

import { use, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import useLocalStorageState from 'use-local-storage-state';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Input from '../../../components/Input/Input';
import Loader from '../../../components/Loader/Loader';

import useSelectedSpace from '../../../hooks/useSelectedSpace';

import UserContext from '../../../contexts/UserContext';

const Divider = () => (
  <div className="h-0 border border-slate-200  dark:border-slate-800 w-full mt-6 md:mt-10 mb-3 md:mb-8" />
);

type ActionError = null | string | string[];

const ErrorBox = ({ error }: { error?: ActionError }) => {
  if (!error) return null;
  return (
    <div className="text-red py-2 inline-flex items-center">
      <div className="block text-left">{error}</div>
    </div>
  );
};

interface UserActionsProps {
  /**
   * User data
   */
  user: User;
  /**
   * On delete user callback
   */
  onDelete: (email: string) => Promise<ActionError>;
  /**
   * On password request send callback
   */
  onPasswordRequest: (email: string) => Promise<ActionError>;
  /**
   * On user activate callback
   */
  onUserActivate: (id: string) => Promise<ActionError>;
  /**
   * On resend activation email callback
   */
  onActivationEmailResend: (id: string) => Promise<ActionError>;
  /**
   * User actions test id
   */
  testId?: string;
  /**
   * User can be deleted
   */
  deletable?: boolean;
  /**
   * Section Tutorial visibility
   */
  isSectionTutorial?: boolean;
}

const UserActions = ({
  user,
  onDelete,
  onPasswordRequest,
  onUserActivate,
  onActivationEmailResend,
  testId,
  deletable = true,
  isSectionTutorial = true,
}: UserActionsProps) => {
  const { isRoleAdmin } = use(UserContext);
  const { t } = useTranslation();
  const [errors, setErrors] = useState<{
    delete?: ActionError;
    password?: ActionError;
    activateUser?: ActionError;
    activationEmailResend?: ActionError;
  }>({});

  const [isDeleting, setIsDeleting] = useState(false);
  const [isTutorialsReady, setIsTutorialsReady] = useState(false);
  const [sendingPswdReq, setSendingPswdReq] = useState(false);
  const [sendingActivatedReq, setSendingActivatedReq] = useState(false);
  const [sendingResendActivationEmailReq, setSendingResendActivationEmailReq] =
    useState(false);
  const [deleteLinkSent, setDeleteLinkSent] = useState(false);
  const [pswdLinkSent, setPswdLinkSent] = useState(false);
  const [, setTutorials] = useLocalStorageState('cms.tutorials');
  const navigate = useNavigate();
  const { buildUrlWithSpace } = useSelectedSpace();

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    const error = await onDelete(user.email);
    if (!error) setDeleteLinkSent(true);
    setErrors((prevErrors) => ({ ...prevErrors, delete: error }));
    setIsDeleting(false);
  }, [onDelete, user.email]);

  const handlePswdChange = useCallback(async () => {
    setSendingPswdReq(true);
    const error = await onPasswordRequest(user.email);
    if (!error) setPswdLinkSent(true);
    setErrors((prevErrors) => ({ ...prevErrors, password: error }));
    setSendingPswdReq(false);
  }, [onPasswordRequest, user.email]);

  const handleTutorialReset = useCallback(async () => {
    setIsTutorialsReady(true);
    setTutorials({ run: true });
    setTimeout(() => {
      setIsTutorialsReady(false);
      navigate(buildUrlWithSpace(''));
    }, 200);
  }, [buildUrlWithSpace, navigate, setTutorials]);

  const handleActivateUser = useCallback(async () => {
    setSendingActivatedReq(true);
    const error = await onUserActivate(user.id);
    if (error) {
      setErrors((prevErrors) => ({ ...prevErrors, activateUser: error }));
    }
    setSendingActivatedReq(false);
  }, [onUserActivate, user]);

  const handleResendActivateEmail = useCallback(async () => {
    setSendingResendActivationEmailReq(true);
    const error = await onActivationEmailResend(user.id);
    if (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        activationEmailResend: error,
      }));
    }
    setSendingResendActivationEmailReq(false);
  }, [onActivationEmailResend, user]);

  const boxClasses =
    'flex flex-col gap-4 md:gap-6 max-w-3xl dark:text-slate-200';
  const headerClasses =
    'text-xl md:text-2xl xl:text-3xl font-semibold dark:text-slate-200';

  return (
    <>
      <div className={boxClasses}>
        <div className={headerClasses}>{t('UserForm.EmailNotification')}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-center">
          <Input
            name="email"
            placeholder={t('Global.Email')}
            label={t('Global.Email')}
            value={user.email}
            additionalInputClasses="bg-white! dark:bg-slate-900!"
            disabled
          />
          <div className="md:mt-5 dark:text-slate-200">
            <Trans i18nKey="UserForm.ChangeEmailInfo">
              <Link
                className="whitespace-nowrap text-blue-600 underline cursor-pointer"
                to="mailto:hello@flotiq.com"
                target="_blank"
                rel="noreferrer"
              >
                hello@flotiq.com
              </Link>
            </Trans>
          </div>
        </div>
      </div>
      <Divider />
      <div className={boxClasses}>
        <div className={headerClasses}>{t('Global.Password')}</div>
        {t('UserForm.ChangePasswordInfo')}
        <div>
          <Button
            disabled={sendingPswdReq || pswdLinkSent}
            iconImage={
              sendingPswdReq ? (
                <Loader size="small" type="spinner-grid" />
              ) : null
            }
            onClick={handlePswdChange}
            {...getTestProps(testId, 'change-password', 'testId')}
          >
            {t('UserForm.ChangePassword')}
          </Button>
          <ErrorBox error={errors.password} />
        </div>
      </div>

      {deletable && (
        <>
          <Divider />
          <div className={boxClasses}>
            <div className={headerClasses}>{t('UserForm.DeleteAccount')}</div>
            {t('UserForm.DeleteAccountInfo')}
            <div>
              <Button
                disabled={isDeleting || deleteLinkSent}
                iconImage={
                  isDeleting ? (
                    <Loader size="small" type="spinner-grid" />
                  ) : null
                }
                onClick={handleDelete}
                {...getTestProps(testId, 'delete-user', 'testId')}
              >
                {t('UserForm.DeleteAccount')}
              </Button>
              <ErrorBox error={errors.delete} />
            </div>
          </div>
        </>
      )}

      {isSectionTutorial && (
        <>
          <Divider />
          <div className={boxClasses}>
            <div className={headerClasses}>{t('UserForm.ResetTutorials')}</div>
            {t('UserForm.ResetTutorialsInfo')}
            <div>
              <Button
                disabled={isTutorialsReady}
                iconImage={
                  isTutorialsReady ? (
                    <Loader size="small" type="spinner-grid" />
                  ) : null
                }
                onClick={handleTutorialReset}
                {...getTestProps(testId, 'reset-tutorial', 'testId')}
              >
                {t('UserForm.ResetTutorials')}
              </Button>
            </div>
          </div>
        </>
      )}
      {isRoleAdmin && (
        <>
          <Divider />
          <div className={boxClasses}>
            <div className={headerClasses}>Activate user</div>
            <div className="py-2">
              <Button
                onClick={handleActivateUser}
                disabled={sendingActivatedReq || user.enabled}
                iconImage={
                  sendingActivatedReq ? (
                    <Loader size="small" type="spinner-grid" />
                  ) : null
                }
                {...getTestProps(testId, 'activate-user')}
              >
                {t('UserForm.ActivateUser')}
              </Button>
              <ErrorBox error={errors.activateUser} />
            </div>
          </div>

          <Divider />

          <div className={boxClasses}>
            <div className={headerClasses}>Resend activation email</div>
            <div className="py-2 my-2">
              <Button
                onClick={handleResendActivateEmail}
                disabled={sendingResendActivationEmailReq || user.enabled}
                iconImage={
                  sendingResendActivationEmailReq ? (
                    <Loader size="small" type="spinner-grid" />
                  ) : null
                }
                {...getTestProps(testId, 'resend-activation-email')}
              >
                {t('UserForm.ResendActivationEmail.Label')}
              </Button>
              <ErrorBox error={errors.activationEmailResend} />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default UserActions;
